import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, Check, Star, ShieldCheck, Cpu, CalendarDays,
  MessageCircle, Stethoscope, Wallet, Video, Trophy, Quote, Menu, X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Pricing } from "@/components/landing/Pricing";
import { Faq } from "@/components/landing/Faq";

// Paleta Sólides: roxo/violeta profundo + verde-limão. Sem preto puro.
const DARK = "bg-gradient-to-br from-[#3b0764] via-[#4c1d95] to-[#2e1065]";
const LIME = "bg-lime-400 text-violet-950 hover:bg-lime-300";

const IMG = {
  hero: "https://images.unsplash.com/photo-1659353888906-adb3e0041693?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  f1: "https://images.unsplash.com/photo-1653566031535-bcf33e1c2893?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  f2: "https://images.unsplash.com/photo-1666886573531-48d2e3c2b684?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  f3: "https://images.unsplash.com/photo-1653565685001-92267a18233a?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  champion: "https://images.unsplash.com/photo-1758691463582-11aea602cd4a?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
  ai: "https://images.unsplash.com/photo-1758691462848-31a39258dbd8?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
  p1: "https://images.unsplash.com/photo-1705909773284-bcbbad9a4023?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  p2: "https://images.unsplash.com/photo-1594824476967-48c8b964273f?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  p3: "https://images.unsplash.com/photo-1758691463384-771db2f192b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  couple: "https://images.unsplash.com/photo-1731419711677-cb2387cfc557?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
  press1: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  press2: "https://images.unsplash.com/photo-1675270855267-3c73bfd13849?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  press3: "https://images.unsplash.com/photo-1516841273335-e39b37888115?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  lead: "https://images.unsplash.com/photo-1612531385446-f7e6d131e1d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=600",
  a1: "https://images.unsplash.com/photo-1758691463582-11aea602cd4a?crop=faces&cs=srgb&fm=jpg&q=85&w=120&h=120&fit=crop",
  a2: "https://images.unsplash.com/photo-1659353888906-adb3e0041693?crop=faces&cs=srgb&fm=jpg&q=85&w=120&h=120&fit=crop",
  a3: "https://images.unsplash.com/photo-1612531385446-f7e6d131e1d0?crop=faces&cs=srgb&fm=jpg&q=85&w=120&h=120&fit=crop",
  a4: "https://images.unsplash.com/photo-1594824476967-48c8b964273f?crop=faces&cs=srgb&fm=jpg&q=85&w=120&h=120&fit=crop",
};

const CTA_PRIMARY = "/agendar";
const HERO_VIDEO_URL = "/hero-video.mp4";
const HERO_POSTER_URL = "/medicacapa.jpg";

const HeroVideo = () => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const start = () => {
    const v = ref.current;
    if (!v) return;
    setPlaying(true);
    v.muted = false;
    v.volume = 1;
    v.play().catch(() => {});
  };
  return (
    <div className="relative h-[420px] w-full overflow-hidden bg-slate-900">
      <video
        ref={ref}
        data-testid="hero-video"
        src={HERO_VIDEO_URL}
        poster={HERO_POSTER_URL}
        playsInline
        controls={playing}
        onPause={() => setPlaying(false)}
        className="h-full w-full object-cover"
      />
      {!playing && (
        <button
          type="button"
          onClick={start}
          data-testid="hero-video-play"
          aria-label="Reproduzir vídeo"
          className="group absolute inset-0 flex cursor-pointer flex-col items-center justify-center gap-4 bg-black/35 transition-colors hover:bg-black/25"
        >
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-lime-400 text-violet-950 shadow-2xl ring-4 ring-white/40 transition-transform duration-300 group-hover:scale-110">
            <svg viewBox="0 0 24 24" fill="currentColor" className="ml-1 h-9 w-9"><path d="M8 5v14l11-7z" /></svg>
          </span>
          <span className="rounded-full bg-black/55 px-4 py-1.5 text-sm font-bold text-white backdrop-blur-sm">
            Assista ao vídeo
          </span>
        </button>
      )}
    </div>
  );
};

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.55, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const Nav = () => {
  const [open, setOpen] = useState(false);
  const links = [
    ["Recursos", "#recursos"], ["Como funciona", "#posicoes"],
    ["Resultados", "#resultados"], ["Planos", "#planos"], ["Dúvidas", "#faq"],
    ["Contato", "#contato"],
  ];
  return (
    <header className={`sticky top-0 z-50 ${DARK} border-b border-white/10`}>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3.5 lg:px-8">
        <a href="#topo" className="flex items-center gap-2.5">
          <img src="/veloclin-logo.png" alt="VeloClin" data-testid="brand-logo" className="h-12 w-auto rounded-full bg-white p-1" />
        </a>
        <nav className="hidden items-center gap-7 lg:flex">
          {links.map(([l, h]) => (
            <a key={h} href={h} className="text-sm font-semibold text-violet-100/80 transition-colors hover:text-white">{l}</a>
          ))}
        </nav>
        <div className="hidden items-center gap-3 lg:flex">
          <Link to="/login" data-testid="nav-enter-app" className="text-sm font-semibold text-white/90 hover:text-white">Entrar</Link>
          <Link to={CTA_PRIMARY} data-testid="nav-cta" className={`flex items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-bold ${LIME} transition-colors`}>
            Agendar Apresentação
          </Link>
        </div>
        <button onClick={() => setOpen((o) => !o)} className="text-white lg:hidden" data-testid="nav-mobile-toggle">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <div className="border-t border-white/10 px-5 pb-4 lg:hidden">
          <div className="flex flex-col gap-1 py-2">
            {links.map(([l, h]) => (
              <a key={h} href={h} onClick={() => setOpen(false)} className="rounded-lg px-2 py-2 text-sm font-semibold text-violet-100/90 hover:bg-white/5">{l}</a>
            ))}
          </div>
          <div className="flex flex-col gap-2">
            <Link to="/login" className="rounded-full border border-white/20 py-2.5 text-center text-sm font-semibold text-white">Entrar</Link>
            <Link to={CTA_PRIMARY} className={`rounded-full py-2.5 text-center text-sm font-bold ${LIME}`}>Agendar Apresentação</Link>
          </div>
        </div>
      )}
    </header>
  );
};

const Hero = () => (
  <section id="topo" className={`relative overflow-hidden ${DARK}`}>
    <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-fuchsia-500/20 blur-3xl" />
    <div className="pointer-events-none absolute -right-16 bottom-0 h-80 w-80 rounded-full bg-lime-400/10 blur-3xl" />
    <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 lg:grid-cols-2 lg:px-8 lg:py-24">
      <div>
        <span className="inline-flex items-center gap-2 rounded-full border border-lime-400/30 bg-lime-400/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-lime-300">
          <Trophy className="h-3.5 w-3.5" /> Feito para o médico independente de todo o Brasil
        </span>
        <h1 className="mt-5 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
          Recupere o controle da sua <span className="text-lime-300">medicina particular</span>
        </h1>
        <p className="mt-5 max-w-xl text-base font-semibold leading-relaxed text-white md:text-lg">
          Menos burocracia, zero taxas abusivas de convênio e o dinheiro da sua consulta direto no seu Pix.
        </p>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-violet-100/85 md:text-base">
          A plataforma completa para médicos independentes em todo o Brasil: prontuário ágil, telemedicina estável, receitas digitais integradas e pagamentos sem intermediários. Por um valor fixo que cabe em meia consulta.
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link to={CTA_PRIMARY} data-testid="hero-cta-primary" className={`flex items-center justify-center gap-2 rounded-full px-7 py-4 text-sm font-bold ${LIME} transition-colors`}>
            Agendar Apresentação <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/cadastro" data-testid="hero-cta-secondary" className="flex items-center justify-center gap-2 rounded-full border border-white/25 px-7 py-4 text-sm font-bold text-white transition-colors hover:bg-white/10">
            Testar gratuitamente
          </Link>
        </div>
        <p className="mt-4 flex items-center gap-2 text-xs text-violet-100/70"><ShieldCheck className="h-4 w-4 text-lime-300" /> Teste por 14 dias grátis · sem cartão de crédito inserido</p>
      </div>
      <Reveal delay={0.1} className="relative">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
          <HeroVideo />
        </div>
        <div className="absolute -bottom-5 -left-5 hidden rounded-2xl border border-white/10 bg-white p-4 shadow-xl sm:block">
          <p className="font-mono-num text-2xl font-extrabold text-violet-700">+38%</p>
          <p className="text-xs font-semibold text-slate-500">faturamento médio</p>
        </div>
      </Reveal>
    </div>
    <div className="border-t border-white/10">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-4 px-5 py-5 text-center sm:grid-cols-3 lg:px-8">
        {[["+27 mil", "consultas por mês na plataforma"], ["R$ 0", "de comissão por consulta"], ["4,9 ★", "avaliação dos médicos"]].map(([a, b]) => (
          <div key={a}>
            <p className="font-heading text-lg font-extrabold text-lime-300">{a}</p>
            <p className="text-xs text-violet-100/70">{b}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Logos = () => (
  <section className="bg-white py-14">
    <div className="mx-auto max-w-6xl px-5 text-center lg:px-8">
      <h2 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
        Somos a escolha de médicos independentes em todo o Brasil
      </h2>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
        {["Cardiologia", "Dermatologia", "Ortopedia", "Pediatria", "Ginecologia", "Psiquiatria", "Clínica Geral"].map((e) => (
          <span key={e} className="rounded-full border border-violet-100 bg-violet-50 px-4 py-2 text-sm font-semibold text-violet-700">{e}</span>
        ))}
      </div>
    </div>
  </section>
);

const FeatureCard = ({ img, tag, title, text }) => (
  <Reveal className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-[0_10px_40px_-15px_rgba(76,29,149,0.25)]">
    <img src={img} alt={title} className="h-48 w-full object-cover" />
    <div className="p-6">
      <span className="text-xs font-bold uppercase tracking-wide text-lime-600">{tag}</span>
      <h3 className="mt-1.5 font-heading text-lg font-bold text-slate-900">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-500">{text}</p>
    </div>
  </Reveal>
);

const Features = () => (
  <section id="recursos" className="bg-white pb-16">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          Tecnologia pronta para a sua <span className="text-violet-700">gestão médica</span>
        </h2>
        <p className="mt-3 text-slate-500">Tudo o que você precisa para atender mais e faturar melhor, em um só lugar.</p>
      </div>
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        <FeatureCard img={IMG.f1} tag="Prontuário & Telemedicina" title="Agilidade clínica de ponta a ponta" text="Atenda de onde estiver com uma telemedicina robusta e um prontuário eletrônico inteligente que reduz o tempo de digitação e protege os dados dos seus pacientes com segurança total." />
        <FeatureCard img={IMG.f2} tag="Receituário" title="Receitas e exames digitais integrados" text="Emita prescrições com validade nacional em segundos através da integração nativa com a Memed, direto na tela do seu computador ou celular." />
        <FeatureCard img={IMG.f3} tag="Financeiro" title="Pagamentos com transparência e agilidade" text="Esqueça retenções complexas. Com a nossa integração de pagamentos, os valores das suas consultas particulares são transferidos a você de forma automática e com toda transparência dentro do seu painel e com total previsibilidade de caixa." />
      </div>
    </div>
  </section>
);

const ChampionBand = () => (
  <section className={`relative overflow-hidden ${DARK}`}>
    <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 py-16 lg:grid-cols-2 lg:px-8">
      <Reveal className="order-2 overflow-hidden rounded-3xl border border-white/10 shadow-2xl lg:order-1">
        <img src={IMG.champion} alt="Médico confiante" className="h-[360px] w-full object-cover" />
      </Reveal>
      <div className="order-1 lg:order-2">
        <h2 className="font-heading text-3xl font-extrabold leading-tight text-white sm:text-4xl">
          Liberdade para o seu consultório, <span className="text-lime-300">do seu jeito</span>
        </h2>
        <p className="mt-4 text-violet-100/85">Chega de perder margem com taxas abusivas de convênios ou plataformas engessadas. A VeloClin foi desenhada para devolver o controle financeiro e o tempo de atendimento ao médico brasileiro, onde quer que ele esteja.</p>
        <div className="mt-8 grid grid-cols-3 gap-4">
          {[["43%", "menos faltas"], ["52%", "mais tempo livre"], ["78%", "mais organização"]].map(([n, l]) => (
            <div key={n} className="rounded-2xl border border-white/10 bg-white/5 p-4 text-center">
              <p className="font-mono-num text-3xl font-extrabold text-lime-300">{n}</p>
              <p className="mt-1 text-xs text-violet-100/70">{l}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link to={CTA_PRIMARY} className={`flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold ${LIME}`}>Agendar Apresentação</Link>
          <Link to="/cadastro" className="flex items-center justify-center rounded-full border border-white/25 px-6 py-3.5 text-sm font-bold text-white hover:bg-white/10">Testar grátis</Link>
        </div>
      </div>
    </div>
  </section>
);

const AiSection = () => (
  <section className="bg-white py-16">
    <div className="mx-auto grid max-w-7xl items-center gap-10 px-5 lg:grid-cols-2 lg:px-8">
      <Reveal className="overflow-hidden rounded-3xl border border-slate-100 shadow-[0_20px_60px_-25px_rgba(76,29,149,0.4)]">
        <img src={IMG.ai} alt="Médica usando o sistema" className="h-[380px] w-full object-cover" />
      </Reveal>
      <div>
        <span className="inline-flex items-center gap-2 rounded-full bg-violet-100 px-3 py-1 text-xs font-bold uppercase tracking-wide text-violet-700"><Cpu className="h-3.5 w-3.5" /> Inteligência Artificial</span>
        <h2 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-slate-900 sm:text-4xl">
          Com a nossa IA, a burocracia não ocupa o seu tempo
        </h2>
        <ul className="mt-6 space-y-3">
          {[
            "Copiloto no WhatsApp confirma e reagenda consultas sozinho",
            "Sua página de vendas atrai e agenda pacientes particulares",
            "Resumos e evolução clínica gerados em segundos",
            "Telemedicina estável e recebimento no Pix na hora",
          ].map((t) => (
            <li key={t} className="flex items-start gap-2.5 text-sm text-slate-600">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-lime-400 text-violet-950"><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>{t}
            </li>
          ))}
        </ul>
        <Link to={CTA_PRIMARY} className="mt-7 inline-flex items-center gap-2 rounded-full bg-violet-700 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-violet-800">
          Agendar Apresentação <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </section>
);

const PositionCard = ({ img, role, title, text }) => (
  <Reveal className="overflow-hidden rounded-3xl border border-white/10 bg-white/5">
    <img src={img} alt={title} className="h-44 w-full object-cover" />
    <div className="p-5">
      <span className="text-xs font-bold uppercase tracking-wide text-lime-300">{role}</span>
      <h3 className="mt-1 font-heading text-base font-bold text-white">{title}</h3>
      <p className="mt-1.5 text-sm text-violet-100/70">{text}</p>
    </div>
  </Reveal>
);

const Positions = () => (
  <section id="posicoes" className={`relative overflow-hidden ${DARK} py-16`}>
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
          Eficiência em cada parte do seu consultório
        </h2>
        <p className="mt-3 text-violet-100/80">Da prática clínica ao crescimento — a ferramenta certa em cada pilar.</p>
      </div>
      <div className="mt-10 grid gap-6 md:grid-cols-3">
        <PositionCard img={IMG.p2} role="Para a Prática Médica" title="Mais tempo com o paciente" text="Foco total no paciente, eliminando a burocracia excessiva pós-atendimento." />
        <PositionCard img={IMG.p1} role="Para a Gestão" title="Caixa e agenda sob controle" text="Visão clara do fluxo de caixa e agenda otimizada sem fricção." />
        <PositionCard img={IMG.p3} role="Para o Crescimento" title="Mais pacientes particulares" text="Ferramentas modernas para atrair e fidelizar pacientes particulares em qualquer cidade do Brasil." />
      </div>
      <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Link to={CTA_PRIMARY} className={`flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold ${LIME}`}>Agendar Apresentação</Link>
        <Link to="/cadastro" className="flex items-center justify-center rounded-full border border-white/25 px-6 py-3.5 text-sm font-bold text-white hover:bg-white/10">Testar grátis</Link>
      </div>
    </div>
  </section>
);

const Impact = () => {
  const items = [
    { img: IMG.a1, name: "Dr. Rafael Mendes", role: "Cardiologia · SP", quote: "Reduzi as faltas em 40% no primeiro mês. A agenda vive cheia.", metric: "+40% presença" },
    { img: IMG.a2, name: "Dra. Camila Ferraz", role: "Dermatologia · RJ", quote: "A telemedicina e a minha página de vendas triplicaram os agendamentos online.", metric: "3x online" },
    { img: IMG.a3, name: "Dr. Thiago Ramos", role: "Ortopedia · MG", quote: "Finalmente entendo meu faturamento e os repasses sem planilha.", metric: "0 planilhas" },
    { img: IMG.a4, name: "Dra. Helena Costa", role: "Pediatria · PA", quote: "Minha secretária economiza horas por dia com a confirmação automática.", metric: "-6h/semana" },
  ];
  return (
    <section id="resultados" className="bg-white py-16">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Milhares de consultas impactadas por uma <span className="text-violet-700">gestão campeã</span>
          </h2>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.05} className="flex flex-col rounded-3xl border border-slate-100 bg-white p-5 shadow-[0_10px_40px_-18px_rgba(76,29,149,0.3)]">
              <div className="flex items-center gap-1 text-amber-400">{[...Array(5)].map((_, k) => <Star key={k} className="h-3.5 w-3.5" fill="currentColor" />)}</div>
              <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">"{t.quote}"</p>
              <span className="mt-3 inline-flex w-fit rounded-full bg-lime-100 px-2.5 py-0.5 text-xs font-bold text-lime-700">{t.metric}</span>
              <div className="mt-4 flex items-center gap-3 border-t border-slate-100 pt-3">
                <img src={t.img} alt={t.name} className="h-10 w-10 rounded-full object-cover" />
                <div>
                  <p className="text-sm font-bold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-400">{t.role}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const QuoteBand = () => (
  <section className={`relative overflow-hidden ${DARK} py-16`}>
    <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 lg:grid-cols-[1.4fr_1fr] lg:px-8">
      <div>
        <Quote className="h-10 w-10 text-lime-300" />
        <p className="mt-4 font-heading text-2xl font-extrabold leading-snug text-white sm:text-3xl">
          "Nosso propósito é potencializar a carreira médica, crescendo lado a lado com cada clínica e cada especialista."
        </p>
        <p className="mt-5 text-sm font-semibold text-violet-100/80">Time VeloClin · desde o primeiro dia</p>
      </div>
      <Reveal className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl">
        <img src={IMG.couple} alt="Equipe médica" className="h-80 w-full object-cover" />
      </Reveal>
    </div>
  </section>
);

const Awards = () => (
  <section className="bg-white py-14">
    <div className="mx-auto max-w-5xl px-5 text-center lg:px-8">
      <h2 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
        A sala de troféus de quem leva a gestão médica a sério
      </h2>
      <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
        {["LGPD Ready", "Assinatura ICP-Brasil", "Pagamentos Stripe", "Telemedicina CFM", "Dados criptografados", "Suporte humano"].map((a) => (
          <div key={a} className="flex items-center gap-2 rounded-2xl border border-violet-100 bg-violet-50 px-4 py-3">
            <ShieldCheck className="h-5 w-5 text-violet-600" />
            <span className="text-sm font-bold text-violet-800">{a}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Press = () => {
  const cards = [
    { img: IMG.press1, tag: "Inovação", title: "A IA que devolve tempo ao médico" },
    { img: IMG.press2, tag: "Gestão", title: "Do agendamento ao faturamento sem planilha" },
    { img: IMG.press3, tag: "Saúde", title: "Telemedicina real, simples e segura" },
  ];
  return (
    <section className="bg-white pb-16">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Referência quando o assunto é <span className="text-violet-700">gestão médica</span>
          </h2>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {cards.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.05} className="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-[0_10px_40px_-18px_rgba(76,29,149,0.3)]">
              <img src={c.img} alt={c.title} className="h-44 w-full object-cover" />
              <div className="p-5">
                <span className="text-xs font-bold uppercase tracking-wide text-lime-600">{c.tag}</span>
                <h3 className="mt-1.5 font-heading text-base font-bold text-slate-900">{c.title}</h3>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const SUBJECTS = ["Parcerias", "Comercial", "Suporte", "Sugestões"];

const LeadForm = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return toast.error("Preencha nome e e-mail.");
    if (!form.subject) return toast.error("Selecione o assunto.");
    if (!form.message.trim()) return toast.error("Escreva sua mensagem.");
    setSending(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Falha ao enviar.");
      toast.success("Mensagem enviada!", { description: "Nossa equipe responderá em breve no seu e-mail." });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Não foi possível enviar", { description: err.message });
    } finally {
      setSending(false);
    }
  };
  return (
    <section id="contato" className={`relative overflow-hidden ${DARK} py-16`}>
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 lg:grid-cols-2 lg:px-8">
        <div>
          <h2 className="font-heading text-3xl font-extrabold leading-tight text-white sm:text-4xl">
            Fale com a <span className="text-lime-300">VeloClin</span>
          </h2>
          <p className="mt-4 text-violet-100/85">Escolha o assunto e envie sua mensagem. Direcionamos para a equipe certa e respondemos no seu e-mail.</p>
          <img src={IMG.lead} alt="Equipe VeloClin" className="mt-8 hidden h-64 w-full rounded-3xl border border-white/10 object-cover lg:block" />
        </div>
        <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl border border-white/10 bg-white p-7 shadow-2xl">
          <h3 className="font-heading text-lg font-bold text-slate-900">Canal de Contato</h3>
          <div className="mt-4 space-y-3">
            <Input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Seu nome" className="border-slate-200 focus-visible:ring-violet-600" />
            <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Seu melhor e-mail" className="border-slate-200 focus-visible:ring-violet-600" />
            <Select value={form.subject} onValueChange={(v) => set("subject", v)}>
              <SelectTrigger data-testid="contact-subject" className="border-slate-200 focus:ring-violet-600"><SelectValue placeholder="Escolha o assunto" /></SelectTrigger>
              <SelectContent>{SUBJECTS.map((s) => <SelectItem key={s} value={s} data-testid={`contact-subject-${s}`}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea data-testid="contact-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={4} placeholder="Escreva sua mensagem..." className="border-slate-200 focus-visible:ring-violet-600" />
          </div>
          <button data-testid="contact-submit" type="submit" disabled={sending} className={`mt-5 flex w-full items-center justify-center gap-2 rounded-full py-3.5 text-sm font-bold ${LIME} transition-colors disabled:opacity-60`}>
            {sending ? "Enviando..." : <>Enviar mensagem <ArrowRight className="h-4 w-4" /></>}
          </button>
          <p className="mt-3 text-center text-xs text-slate-400">Ou <Link to="/cadastro" data-testid="final-cta" className="font-bold text-violet-700 hover:underline">comece grátis agora</Link></p>
        </form>
      </div>
    </section>
  );
};

const SiteFooter = () => (
  <footer className={`${DARK} border-t border-white/10`}>
    <div className="mx-auto grid max-w-7xl gap-8 px-5 py-12 sm:grid-cols-2 lg:grid-cols-4 lg:px-8">
      <div>
        <div className="flex items-center gap-2.5">
          <img src="/veloclin-logo.png" alt="VeloClin" data-testid="brand-logo" className="h-14 w-auto rounded-full bg-white p-1" />
        </div>
        <p className="mt-3 max-w-xs text-sm text-violet-100/70">Gestão e alavancagem completa para médicos e clínicas.</p>
      </div>
      {[
        ["Produto", [["Recursos", "#recursos"], ["Planos", "#planos"], ["Dúvidas", "#faq"], ["Contato", "#contato"]]],
        ["Comece agora", [["Testar grátis", "/cadastro"], ["Sou médico Solo", "/cadastro/solo?plan=solo"], ["Entrar", "/login"]]],
        ["Recursos", [["Agenda & Recepção", "#recursos"], ["Telemedicina", "#recursos"], ["Financeiro", "#recursos"]]],
      ].map(([title, links]) => (
        <div key={title}>
          <p className="font-heading text-sm font-bold text-white">{title}</p>
          <ul className="mt-3 space-y-2">
            {links.map(([l, h]) => (
              <li key={l}>
                {h.startsWith("#")
                  ? <a href={h} className="text-sm text-violet-100/70 hover:text-white">{l}</a>
                  : <Link to={h} data-testid={l === "Entrar" ? "footer-enter-app" : undefined} className="text-sm text-violet-100/70 hover:text-white">{l}</Link>}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
    <div className="border-t border-white/10 py-5 text-center text-xs text-violet-100/50">
      © 2026 VeloClin · Todos os direitos reservados
    </div>
  </footer>
);

export default function SolidesLanding() {
  return (
    <div className="min-h-screen overflow-x-hidden bg-white">
      <Nav />
      <main>
        <Hero />
        <Logos />
        <Features />
        <ChampionBand />
        <AiSection />
        <Positions />
        <Impact />
        <QuoteBand />
        <Awards />
        <Press />
        <div id="planos" className="bg-white"><Pricing /></div>
        <div id="faq"><Faq /></div>
        <LeadForm />
      </main>
      <SiteFooter />
    </div>
  );
}
